import { useMask } from "@react-input/mask";
import clsx from "clsx";
import { omit } from "lodash-es";
import {
  HTMLInputTypeAttribute,
  MutableRefObject,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import CurrencyInput from "react-currency-input-field";
import { IconProps } from "react-toastify";

import { Icon, type IconName } from "@/components/common/Icon";
import { LoadingSkeleton } from "@/components/common/LoadingSkeleton";
import { useElement } from "@/hooks/common/use-element";
import { useInput } from "@/hooks/common/use-input";
import { DefaultComponentPropsWith } from "@/interfaces/common";
import { CommonInputProps, FormControl } from "@/interfaces/form";
import { isElement } from "@/utils";

import { InputError } from "../InputError";
import { handleNumericKeyDown, handleNumericPaste } from "./utils";

export interface InputValueProps extends DefaultComponentPropsWith<CommonInputProps> {
  field: string;
  label?: string;
  control: FormControl;
  outlined?: boolean;
  underlined?: boolean;
  invert?: boolean;
  disabled?: boolean;
  leading?: ReactElement | IconName;
  trailing?: ReactElement | IconName;
  type?: HTMLInputTypeAttribute;
  maxLength?: number;
  showMaxLength?: boolean;
  value?: string | number;
  inputClassName?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  shownValue?: () => string | number;
  mask?: "brl-currency" | "cpf" | "rg" | "cep" | "brazilian-phone" | "date";
  iconPasswordVariant?: "primary" | "tertiary";
  iconLeft?: () => ReactElement;
  iconRight?: () => ReactElement;
  minValue?: number;
  maxValue?: number;
  onlyIntegers?: boolean;
}

export const InputValue = ({
  type,
  children,
  field,
  label,
  className,
  control,
  outlined = true,
  underlined = false,
  invert = false,
  disabled = false,
  leading,
  trailing,
  maxLength,
  showMaxLength = false,
  value,
  inputClassName,
  onChange,
  onBlur,
  shownValue,
  mask,
  iconPasswordVariant = "primary",
  iconLeft,
  iconRight,
  minValue,
  maxValue,
  onlyIntegers,
  ...attributes
}: InputValueProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [Leading, Trailing] = useElement<Partial<IconProps>>(
    {},
    isElement(leading) ? leading : <Icon name={leading} />,
    isElement(trailing) ? trailing : <Icon name={trailing} />,
  );

  const options = useMemo(() => {
    switch (mask) {
      case "brl-currency":
        return {};
      case "cpf":
        return {
          mask: "___.___.___-__",
          replacement: { _: /\d/ },
        };
      case "rg":
        return {
          mask: "__.___.___-_",
          replacement: { _: /\d/ },
        };
      case "cep":
        return {
          mask: "_____-___",
          replacement: { _: /\d/ },
        };
      case "brazilian-phone":
        return {
          mask: "(__) _____-____",
          replacement: { _: /\d/ },
        };
      case "date":
        return {
          mask: "__/__/____",
          replacement: { _: /\d/ },
        };
      default:
        return {};
    }
  }, [mask]);
  const inputMaskRef = useMask(options);

  const { errorMessage, registration } = useInput({
    control,
    field,
    shouldRegister: true,
  });
  const registrationRef = useRef(registration);
  registrationRef.current = registration;

  const setInputReference = useCallback(
    (element: HTMLInputElement | null) => {
      registrationRef.current.ref(element);

      if (element === null) {
        const previousInput = inputRef.current;
        if (previousInput && previousInput === document.activeElement) {
          previousInput.blur();
        }

        const maskReference = inputMaskRef as MutableRefObject<HTMLInputElement | null>;
        maskReference.current = null;
        inputRef.current = null;
        return;
      }

      inputRef.current = element;
      if (Object.keys(options).length > 0) {
        inputMaskRef.current = element;
      }
    },
    [control, field, inputMaskRef, options],
  );

  const borderStyle = useMemo(() => {
    if (errorMessage) {
      return "border-error" + (invert ? " bg-transparent text-background-primary" : "");
    }

    if (isFocused) {
      return "border-primary";
    }

    if (!outlined && !underlined) {
      return "border-transparent";
    }

    if (invert) {
      return "border-background-primary bg-transparent text-background-primary";
    }

    return "border-input-border";
  }, [errorMessage, invert, outlined, underlined, isFocused]);

  useEffect(() => {
    if (value === undefined || control.getValue(field) === value) {
      return;
    }

    control.onChange({ target: { name: field, value } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, field, control.onChange, control.getValue]);

  const inputClasses = clsx(
    "input w-full border-none",
    {
      "bg-background-primary": outlined && !underlined,
      "bg-input-background": !outlined && !underlined,
      "opacity-50": disabled,
    },
    inputClassName,
  );

  const VisibilityIcon: IconName = isPasswordVisible ? "PasswordVisibilityOff" : "PasswordVisibility";

  const inputType = useMemo(() => {
    if (isPasswordVisible) {
      return "text";
    }

    if (shownValue) {
      return "hidden";
    }

    return type;
  }, [isPasswordVisible, shownValue, type]);

  return (
    <div className={clsx("relative flex flex-col", className)}>
      {label && (
        <label
          className={clsx("label", {
            "opacity-50": disabled,
          })}
          htmlFor={field}
        >
          {label}
        </label>
      )}

      {control.isFetching ? (
        <LoadingSkeleton kind="inputWithoutLabel" />
      ) : (
        <div
          className={clsx(
            "flex w-full items-center justify-between overflow-hidden border-b-input-width",
            borderStyle,
            {
              "input-outlined bg-background-primary": outlined,
              "input-filled bg-input-background": !outlined,
              "hover:border-b-input-border-focus": !disabled,
            },
          )}
        >
          {Leading && (
            <div className="max-w-10 pl-4">
              <Leading />
            </div>
          )}

          {mask === "brl-currency" ? (
            // Input com máscara de moeda
            <CurrencyInput
              {...attributes}
              {...omit(registration, ["ref"])}
              defaultValue={control.getValue(field)}
              decimalScale={2}
              decimalsLimit={2}
              allowNegativeValue={false}
              className={inputClasses}
              disabled={disabled}
              intlConfig={{ locale: "pt-BR", currency: "BRL" }}
              onValueChange={(_, __, { float } = { float: 0, formatted: "", value: "" }) => {
                control.onChange({ target: { name: field, value: float } });
              }}
            />
          ) : (
            // Input padrão
            <>
              {iconLeft?.()}
              <input
                {...attributes}
                {...registration}
                id={field}
                type={inputType}
                min={minValue}
                max={maxValue}
                ref={setInputReference}
                onFocus={() => setIsFocused(true)}
                onBlur={(event) => {
                  registration.onBlur(event);
                  setIsFocused(false);
                  onBlur?.(event);
                }}
                onChange={(event) => {
                  onChange?.(event);
                  registration.onChange(event);
                }}
                onKeyDown={(event) => (onlyIntegers ? handleNumericKeyDown(event, minValue, maxValue) : undefined)}
                onPaste={(event) => (onlyIntegers ? handleNumericPaste(event, minValue, maxValue) : undefined)}
                className={inputClasses}
                disabled={disabled}
                maxLength={maxLength}
                value={value}
              >
                {children}
              </input>

              {shownValue && (
                <input
                  type={isPasswordVisible ? "text" : type}
                  className={inputClasses}
                  placeholder={attributes.placeholder}
                  value={shownValue?.()}
                  readOnly
                >
                  {children}
                </input>
              )}
            </>
          )}

          {type === "password" && (
            <div
              role="none"
              className="cursor-pointer pr-4 text-body"
              onClick={() => setIsPasswordVisible(!isPasswordVisible)}
            >
              <Icon
                name={VisibilityIcon}
                className={clsx(
                  iconPasswordVariant === "primary" && "text-primary",
                  iconPasswordVariant === "tertiary" && "text-tertiary",
                )}
              />
            </div>
          )}

          {iconRight?.()}

          {Trailing && (
            <div className="pr-4">
              <Trailing />
            </div>
          )}
        </div>
      )}

      {!!(errorMessage || maxLength) && (
        <div className="mt-2 flex justify-between gap-2">
          <InputError message={errorMessage} className="mt-0" />

          {maxLength && showMaxLength && (
            <div className="ml-auto">
              {control.watch(field)?.length ?? 0}/{maxLength}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
